import { Component, OnInit } from '@angular/core';
import { DashboardService } from './dashboard.service';
import Swal from 'sweetalert2';
import { TutorialService } from '../../core/tutorial.service';
import { MatDialog } from '@angular/material/dialog';
import { TutorialDialogComponent, TutorialItem } from '../../shared/tutorial-dialog/tutorial-dialog.component';
import { AuthService } from '../../core/auth.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  loading = false;
  data: any = null;
  lastUpdated = new Date();

  constructor(
    private dashboard: DashboardService,
    private tutorial: TutorialService,
    private dialog: MatDialog,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  loadDashboard(): void {
    this.loading = true;
    this.dashboard.getSummary().subscribe({
      next: (res) => {
        this.data = this.normalize(res);
        this.loading = false;
        this.lastUpdated = new Date();
        this.maybeShowTutorial();
      },
      error: (err) => {
        this.loading = false;
        console.error('[Dashboard] ❌ Load failed:', err);
        Swal.fire('Error', 'Failed to load dashboard summary.', 'error');
      }
    });
  }

  get percentPaid(): number {
    if (!this.data || this.data.delivery.total === 0) return 0;
    return Math.round((this.data.delivery.paid / this.data.delivery.total) * 100);
  }

  get userName(): string {
    return this.auth.getUser()?.fullName || 'Team';
  }

  get commandStatus(): string {
    if (!this.data) return 'Loading';
    if (this.data.maintenance.totalFlagged > 0 || this.data.delivery.pending > this.data.delivery.paid) return 'Attention Needed';
    if (this.data.notifications.unreadCount > 0 || this.data.staff.pendingRequests > 0) return 'Monitoring';
    return 'Stable';
  }

  get commandTone(): 'alert' | 'watch' | 'stable' {
    if (!this.data) return 'watch';
    if (this.data.maintenance.totalFlagged > 0 || this.data.delivery.pending > this.data.delivery.paid) return 'alert';
    if (this.data.notifications.unreadCount > 0 || this.data.staff.pendingRequests > 0) return 'watch';
    return 'stable';
  }

  get moduleLinks(): Array<{ title: string; route: string; description: string; metric: string; tone: string }> {
    if (!this.data) return [];

    const links = [
      this.auth.hasAnyPermission(['project.view', 'project.manage', 'project.task.manage', 'project.onboarding.manage', 'project.client.onboarding', 'client']) ? {
        title: 'Projects',
        route: '/projects/overview',
        description: 'Track delivery health, timelines, risks, and document readiness.',
        metric: `${this.data.projects.activeProjects} active`,
        tone: 'projects'
      } : null,
      this.auth.hasAnyPermission(['staff.view', 'staff.manage', 'staff.request.view', 'staff.request.approve']) ? {
        title: 'Staff',
        route: '/staff/overview',
        description: 'Monitor workforce activity and request queues.',
        metric: `${this.data.staff.pendingRequests} pending requests`,
        tone: 'staff'
      } : null,
      this.auth.hasAnyPermission(['staff.message.view', 'staff.message.send', 'staff.manage']) ? {
        title: 'Communication Hub',
        route: '/communication/mails',
        description: 'Open mails, chats, and call workflows for internal communication.',
        metric: 'Mails, chats, calls',
        tone: 'staff'
      } : null,
      this.auth.hasAnyPermission(['manage_logistics', 'manage_delivery', 'manage_riders', 'manage_vehicles', 'manage_maintenance', 'manage_activities', 'dispatcher', 'rider']) ? {
        title: 'Logistics',
        route: '/logistics/overview',
        description: 'Run dispatch, rider readiness, and fleet pressure from one board.',
        metric: `${this.data.delivery.today} deliveries today`,
        tone: 'logistics'
      } : null,
      this.auth.hasAnyPermission(['manage_supply', 'supply.products.manage', 'supply.dispatch.manage', 'supply.history.view', 'supply.analytics.view']) ? {
        title: 'Supply',
        route: '/supply/overview',
        description: 'Review stock movement, dispatch value, and catalog performance.',
        metric: `${this.data.supply.totalDispatches} dispatches`,
        tone: 'supply'
      } : null
    ];

    return links.filter((entry): entry is { title: string; route: string; description: string; metric: string; tone: string } => !!entry);
  }

  get attentionItems(): Array<{ label: string; value: string; tone: string }> {
    if (!this.data) return [];
    return [
      { label: 'Pending Deliveries', value: String(this.data.delivery.pending || 0), tone: 'warning' },
      { label: 'Flagged Vehicles', value: String(this.data.maintenance.totalFlagged || 0), tone: 'danger' },
      { label: 'Pending Staff Requests', value: String(this.data.staff.pendingRequests || 0), tone: 'watch' },
      { label: 'Unread Alerts', value: String(this.data.notifications.unreadCount || 0), tone: 'accent' }
    ];
  }

  get deliveryDistribution() {
    if (!this.data?.delivery?.total) return [];
    const total = this.data.delivery.total;
    return [
      { label: 'Paid', value: this.data.delivery.paid || 0, color: '#0f766e', pct: this.percent(this.data.delivery.paid, total) },
      { label: 'Pending', value: this.data.delivery.pending || 0, color: '#c2410c', pct: this.percent(this.data.delivery.pending, total) },
      { label: 'Cancelled', value: this.data.delivery.cancelled || 0, color: '#64748b', pct: this.percent(this.data.delivery.cancelled, total) }
    ];
  }

  get revenueSeries() {
    if (!this.data?.revenue) return [];
    return [
      { label: 'Today', value: this.data.revenue.today || 0 },
      { label: 'Week', value: this.data.revenue.thisWeek || 0 },
      { label: 'Month', value: this.data.revenue.thisMonth || 0 },
      { label: 'Year', value: this.data.revenue.thisYear || 0 }
    ];
  }

  get maxRevenueValue(): number {
    const values = this.revenueSeries.map(r => r.value);
    return values.length ? Math.max(...values, 1) : 1;
  }

  get vehicleAssignPercent(): number {
    if (!this.data?.vehicles?.total) return 0;
    return this.percent(this.data.vehicles.assigned, this.data.vehicles.total);
  }

  get riderActivePercent(): number {
    if (!this.data?.riders?.total) return 0;
    return this.percent(this.data.riders.active, this.data.riders.total);
  }

  get meterClosePercent(): number {
    if (!this.data?.vehicles?.meterOpenedToday) return 0;
    return this.percent(this.data.vehicles.meterClosedToday, this.data.vehicles.meterOpenedToday);
  }

  get phoneClosePercent(): number {
    if (!this.data?.riders?.phoneOpenedToday) return 0;
    return this.percent(this.data.riders.phoneClosedToday, this.data.riders.phoneOpenedToday);
  }

  get grossMarginThisMonth(): number {
    if (!this.data?.revenue) return 0;
    const revenue = this.data.revenue.thisMonth || 0;
    const cost = this.data.revenue.totalCostOfDelivery || 0;
    const commission = this.data.revenue.totalRiderCommission || 0;
    return revenue - cost - commission;
  }

  get supplyCommissionRate(): number {
    if (!this.data?.supply?.grossAmount) return 0;
    return this.percent(this.data.supply.totalCommission, this.data.supply.grossAmount);
  }

  get staffRequestApprovalRate(): number {
    if (!this.data?.staff?.totalRequests) return 0;
    return this.percent(this.data.staff.approvedRequests, this.data.staff.totalRequests);
  }

  get projectTaskCompletionRate(): number {
    if (!this.data?.projects?.totalTasks) return 0;
    return this.percent(this.data.projects.completedTasks, this.data.projects.totalTasks);
  }

  get maintenanceMix() {
    if (!this.data?.maintenance) return { services: 0, repairs: 0, total: 0 };
    const services = this.data.maintenance.serviceCount || 0;
    const repairs = this.data.maintenance.repairCount || 0;
    return { services, repairs, total: Math.max(services + repairs, 1) };
  }

  revenueBarHeight(value: number): number {
    return Math.max(10, Math.round((value / this.maxRevenueValue) * 100));
  }

  maybeShowTutorial(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 900) {
      return;
    }

    const user = this.auth.getUser() as any;
    const userId = user?.id || user?.userId || null;
    const area = 'dashboard';
    if (!this.tutorial.shouldShow(userId, area)) return;

    const items: TutorialItem[] = [];

    items.push({
      title: 'Dashboard Overview',
      description: 'Use Refresh to pull live KPIs. Distance and compliance cards show today and 30-day fleet health.'
    });

    if (this.auth.hasPermission('staff.manage') || this.auth.hasPermission('staff.request.approve')) {
      items.push({
        title: 'Payroll & Staff',
        description: 'Go to Staff → Payroll to set per-role rates, delivery pricing, and generate slips.',
        actions: ['Use Manual Units or Fixed Amount for special payouts', 'Toggle supply/activity counts as needed']
      });
    }

    if (this.auth.hasPermission('project.view') || this.auth.hasPermission('project.task.manage')) {
      items.push({
        title: 'Projects & My Todo',
        description: 'Check Projects → My Todo for your assigned tasks; use Tasks/Analytics for project progress.',
        actions: ['Create tasks in Projects → Tasks', 'Clients can review onboarding PDFs']
      });
    }

    if (this.auth.hasPermission('manage_logistics')) {
      items.push({
        title: 'Logistics & Maintenance',
        description: 'Record meter/fuel and phone logs daily. Maintenance page lists distances and recent logs.',
        actions: ['Open/close meter and phone logs within 15 minutes', 'View meter/phone history tables in Maintenance']
      });
    }

    if (items.length === 0) return;

    const ref = this.dialog.open(TutorialDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'tutorial-dialog-panel',
      data: { items, title: 'Quick Tour' }
    });

    ref.afterClosed().subscribe((dontShow: boolean) => {
      if (dontShow) this.tutorial.dismiss(userId, area);
    });
  }

  private percent(part: number, total: number): number {
    if (!total) return 0;
    return Math.round((part / total) * 100);
  }

  private normalize(res: any): any {
    const safe = res || {};
    return {
      delivery: {
        total: safe.delivery?.total ?? 0,
        today: safe.delivery?.today ?? 0,
        paid: safe.delivery?.paid ?? 0,
        pending: safe.delivery?.pending ?? 0,
        cancelled: safe.delivery?.cancelled ?? 0
      },
      vehicles: {
        total: safe.vehicles?.total ?? 0,
        assigned: safe.vehicles?.assigned ?? 0,
        unassigned: safe.vehicles?.unassigned ?? 0,
        meterOpenedToday: safe.vehicles?.meterOpenedToday ?? 0,
        meterClosedToday: safe.vehicles?.meterClosedToday ?? 0,
        meterPendingClose: safe.vehicles?.meterPendingClose ?? 0,
        totalDistanceToday: safe.vehicles?.totalDistanceToday ?? 0,
        averageDistanceToday: safe.vehicles?.averageDistanceToday ?? 0,
        totalDistance30: safe.vehicles?.totalDistance30 ?? 0,
        averageDistance30: safe.vehicles?.averageDistance30 ?? 0,
        averageStartFuelLevel: safe.vehicles?.averageStartFuelLevel ?? 0,
        averageEndFuelLevel: safe.vehicles?.averageEndFuelLevel ?? 0,
        averageFuelUsed: safe.vehicles?.averageFuelUsed ?? 0
      },
      riders: {
        total: safe.riders?.total ?? 0,
        active: safe.riders?.active ?? 0,
        inactive: safe.riders?.inactive ?? 0,
        phoneOpenedToday: safe.riders?.phoneOpenedToday ?? 0,
        phoneClosedToday: safe.riders?.phoneClosedToday ?? 0,
        phonePendingClose: safe.riders?.phonePendingClose ?? 0,
        totalCreditUsedToday: safe.riders?.totalCreditUsedToday ?? 0,
        averageCreditUsedToday: safe.riders?.averageCreditUsedToday ?? 0
      },
      maintenance: {
        totalFlagged: safe.maintenance?.totalFlagged ?? 0,
        dueThisWeek: safe.maintenance?.dueThisWeek ?? 0,
        totalRecords: safe.maintenance?.totalRecords ?? 0,
        serviceCount: safe.maintenance?.serviceCount ?? 0,
        repairCount: safe.maintenance?.repairCount ?? 0,
        totalCost: safe.maintenance?.totalCost ?? 0,
        thisMonthCost: safe.maintenance?.thisMonthCost ?? 0
      },
      supply: {
        totalDispatches: safe.supply?.totalDispatches ?? 0,
        todayDispatches: safe.supply?.todayDispatches ?? 0,
        totalUnits: safe.supply?.totalUnits ?? 0,
        grossAmount: safe.supply?.grossAmount ?? 0,
        totalCommission: safe.supply?.totalCommission ?? 0,
        netAmount: safe.supply?.netAmount ?? 0,
        thisMonthGrossAmount: safe.supply?.thisMonthGrossAmount ?? 0
      },
      staff: {
        totalStaff: safe.staff?.totalStaff ?? 0,
        activeStaff: safe.staff?.activeStaff ?? 0,
        inactiveStaff: safe.staff?.inactiveStaff ?? 0,
        totalRequests: safe.staff?.totalRequests ?? 0,
        pendingRequests: safe.staff?.pendingRequests ?? 0,
        approvedRequests: safe.staff?.approvedRequests ?? 0,
        rejectedRequests: safe.staff?.rejectedRequests ?? 0,
        fundsRequestedAmount: safe.staff?.fundsRequestedAmount ?? 0,
        loanRequestedAmount: safe.staff?.loanRequestedAmount ?? 0,
        leaveRequests: safe.staff?.leaveRequests ?? 0
      },
      projects: {
        totalProjects: safe.projects?.totalProjects ?? 0,
        activeProjects: safe.projects?.activeProjects ?? 0,
        completedProjects: safe.projects?.completedProjects ?? 0,
        totalTasks: safe.projects?.totalTasks ?? 0,
        completedTasks: safe.projects?.completedTasks ?? 0,
        pendingTasks: safe.projects?.pendingTasks ?? 0,
        averageProjectProgress: safe.projects?.averageProjectProgress ?? 0,
        totalOnboardingItems: safe.projects?.totalOnboardingItems ?? 0
      },
      notifications: {
        unreadCount: safe.notifications?.unreadCount ?? 0,
        recent: safe.notifications?.recent ?? []
      },
      revenue: {
        today: safe.revenue?.today ?? 0,
        thisWeek: safe.revenue?.thisWeek ?? 0,
        thisMonth: safe.revenue?.thisMonth ?? 0,
        thisYear: safe.revenue?.thisYear ?? 0,
        pending: safe.revenue?.pending ?? 0,
        totalCostOfDelivery: safe.revenue?.totalCostOfDelivery ?? 0,
        totalRiderCommission: safe.revenue?.totalRiderCommission ?? 0
      }
    };
  }
}
